import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useBusiness } from "@/providers/business-provider";
import { Sidebar } from "@/components/ui/sidebar";
import {
  TrendingUp,
  TrendingDown,
  Brain,
  AlertTriangle,
  CheckCircle,
  Target,
  BarChart3,
  Calendar,
  DollarSign,
  Lightbulb,
  Activity,
  Zap,
  Settings,
  Play,
  RefreshCw
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";

interface BudgetForecast {
  id: number;
  categoryName: string;
  forecastPeriod: string;
  predictedAmount: number;
  confidenceScore: number;
  trendDirection: 'increasing' | 'decreasing' | 'stable';
  volatilityScore: number;
  scenario: {
    name: string;
    type: string;
    confidenceLevel: number;
  };
}

interface AIInsight {
  id: number;
  type: 'trend' | 'anomaly' | 'recommendation' | 'alert';
  severity: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  actionable: boolean;
  suggestedActions: string[];
  confidenceScore: number;
  createdAt: string;
}

interface ScenarioComparison {
  category: string;
  baseline: number;
  optimistic: number;
  pessimistic: number;
  period: string;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AIForecastingDashboard() {
  const { activeBusiness } = useBusiness();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedPeriods, setSelectedPeriods] = useState(12);
  const [selectedScenarios, setSelectedScenarios] = useState(['baseline', 'optimistic', 'pessimistic']);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch existing forecasts
  const { data: forecasts, isLoading: forecastsLoading } = useQuery({
    queryKey: ['/api/budgets/forecasts', activeBusiness?.id],
    enabled: !!activeBusiness?.id
  });

  // Fetch AI insights
  const { data: insights, isLoading: insightsLoading } = useQuery({
    queryKey: ['/api/budgets/insights', activeBusiness?.id],
    enabled: !!activeBusiness?.id
  });

  // State to store generated forecasts
  const [generatedForecasts, setGeneratedForecasts] = useState<any>(null);

  // Generate new forecasts mutation
  const generateForecastsMutation = useMutation({
    mutationFn: async (params: any) => {
      const response = await apiRequest("POST", "/api/budgets/generate-forecasts", params);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch (e) {
        console.error('Failed to parse JSON response:', text);
        throw new Error('Invalid JSON response from server');
      }
    },
    onSuccess: (data) => {
      console.log("Generated forecasts data:", data);
      setGeneratedForecasts(data);
      toast({
        title: "AI Forecasts Generated", 
        description: "Dynamic budget forecasts have been successfully generated with market analysis.",
      });
      // Force re-render by invalidating queries
      queryClient.invalidateQueries({ queryKey: ['/api/budgets/forecasts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/budgets/insights'] });
    },
    onError: (error: any) => {
      toast({
        title: "Generation Failed",
        description: error.message || "Failed to generate AI forecasts.",
        variant: "destructive",
      });
    },
  });

  const handleGenerateForecasts = async () => {
    if (!activeBusiness?.id) return;
    
    setIsGenerating(true);
    try {
      await generateForecastsMutation.mutateAsync({
        forecastPeriods: selectedPeriods,
        confidenceLevel: 0.85,
        includeMarketFactors: true,
        scenarioTypes: selectedScenarios
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Process forecast data for visualization - use generated data if available
  const processedForecasts: BudgetForecast[] = generatedForecasts?.forecasts || [];
  const scenarios = generatedForecasts?.scenarios || [];
  const aiInsights = generatedForecasts?.insights || [];
  
  console.log("Current forecast data:", {
    generatedForecasts,
    processedForecasts: processedForecasts.length,
    scenarios: scenarios.length,
    insights: aiInsights.length
  });

  // Create scenario comparison data
  const scenarioComparison: ScenarioComparison[] = [];
  if (processedForecasts.length > 0) {
    const categories = Array.from(new Set(processedForecasts.map((f: BudgetForecast) => f.categoryName)));
    const periods = Array.from(new Set(processedForecasts.map((f: BudgetForecast) => f.forecastPeriod))).slice(0, 6);
    
    periods.forEach(period => {
      categories.forEach(category => {
        const baselineData = processedForecasts.find((f: BudgetForecast) => 
          f.categoryName === category && f.forecastPeriod === period && f.scenario?.type === 'baseline'
        );
        const optimisticData = processedForecasts.find((f: BudgetForecast) => 
          f.categoryName === category && f.forecastPeriod === period && f.scenario?.type === 'optimistic'
        );
        const pessimisticData = processedForecasts.find((f: BudgetForecast) => 
          f.categoryName === category && f.forecastPeriod === period && f.scenario?.type === 'pessimistic'
        );

        if (baselineData) {
          scenarioComparison.push({
            category,
            period,
            baseline: baselineData.predictedAmount,
            optimistic: optimisticData?.predictedAmount || baselineData.predictedAmount,
            pessimistic: pessimisticData?.predictedAmount || baselineData.predictedAmount
          });
        }
      });
    });
  }

  // Create trend analysis data
  const trendData = processedForecasts
    .filter((f: any) => f.scenario?.type === 'conservative')
    .slice(0, 12)
    .map((f: any) => ({
      period: f.forecastPeriod,
      amount: f.predictedAmount,
      confidence: f.confidence * 100,
      category: f.categoryName,
      trend: 'up'
    }));

  // Critical insights filter
  const criticalInsights = aiInsights.filter((insight: AIInsight) => 
    insight.severity === 'high' || insight.severity === 'critical'
  );

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'destructive';
      case 'high': return 'destructive';
      case 'medium': return 'secondary';
      case 'low': return 'outline';
      default: return 'outline';
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'trend': return TrendingUp;
      case 'anomaly': return AlertTriangle;
      case 'recommendation': return Lightbulb;
      case 'alert': return AlertTriangle;
      default: return Brain;
    }
  };

  if (!activeBusiness) {
    return (
      <div className="flex h-screen bg-background">
        <Sidebar />
        <div className="flex-1 md:ml-64 flex items-center justify-center">
          <p className="text-muted-foreground">Please select a business to view AI forecasting.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      <div className="flex-1 md:ml-64 space-y-6 p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Brain className="h-8 w-8 text-blue-600" />
              AI Budget Forecasting
            </h1>
            <p className="text-muted-foreground">
              Dynamic budget forecasts powered by AI analysis of historical data and market trends
            </p>
          </div>
          
          <Dialog>
            <DialogTrigger asChild>
              <Button className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Generate Forecasts
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Generate AI Forecasts</DialogTitle>
                <DialogDescription>
                  Configure parameters for AI-powered budget forecasting
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="periods">Forecast Periods (months)</Label>
                  <Select value={selectedPeriods.toString()} onValueChange={(value) => setSelectedPeriods(parseInt(value))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select periods" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="6">6 months</SelectItem>
                      <SelectItem value="12">12 months</SelectItem>
                      <SelectItem value="18">18 months</SelectItem>
                      <SelectItem value="24">24 months</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Scenario Types</Label>
                  <div className="flex gap-2 mt-2">
                    {['baseline', 'optimistic', 'pessimistic'].map(scenario => (
                      <Button
                        key={scenario}
                        variant={selectedScenarios.includes(scenario) ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => {
                          if (selectedScenarios.includes(scenario)) {
                            setSelectedScenarios(prev => prev.filter(s => s !== scenario));
                          } else {
                            setSelectedScenarios(prev => [...prev, scenario]);
                          }
                        }}
                      >
                        {scenario.charAt(0).toUpperCase() + scenario.slice(1)}
                      </Button>
                    ))}
                  </div>
                </div>
                
                <Button 
                  onClick={handleGenerateForecasts}
                  disabled={isGenerating || selectedScenarios.length === 0}
                  className="w-full"
                >
                  {isGenerating ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Generating AI Forecasts...
                    </>
                  ) : (
                    <>
                      <Play className="h-4 w-4 mr-2" />
                      Generate Forecasts
                    </>
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Critical Insights Banner */}
        {criticalInsights.length > 0 && (
          <Card className="border-red-200 bg-red-50">
            <CardHeader className="pb-3">
              <CardTitle className="text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Critical Insights Require Attention
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {criticalInsights.slice(0, 2).map((insight: AIInsight) => (
                  <div key={insight.id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                    <div>
                      <p className="font-medium text-red-800">{insight.title}</p>
                      <p className="text-sm text-red-600">{insight.description}</p>
                    </div>
                    <Badge variant="destructive">{insight.severity}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Show Generated Forecasts */}
        {processedForecasts.length > 0 && (
          <Card className="border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="text-green-800 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                AI Forecasts Generated Successfully
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {processedForecasts.slice(0, 6).map((forecast: any, index: number) => (
                  <div key={index} className="bg-white p-4 rounded-lg border">
                    <div className="flex justify-between items-start">
                      <div>
                        <h4 className="font-semibold text-green-800">{forecast.categoryName}</h4>
                        <p className="text-sm text-green-600">{forecast.forecastPeriod}</p>
                      </div>
                      <Badge variant="outline" className="text-green-700">
                        {Math.round(forecast.confidence * 100)}% confidence
                      </Badge>
                    </div>
                    <div className="mt-2">
                      <p className="text-2xl font-bold text-green-900">
                        ${forecast.predictedAmount.toLocaleString()}
                      </p>
                      <p className="text-xs text-green-600">
                        {forecast.scenario?.name || 'Scenario'}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Dashboard */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="scenarios">Scenario Analysis</TabsTrigger>
            <TabsTrigger value="trends">Trend Analysis</TabsTrigger>
            <TabsTrigger value="insights">AI Insights</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Forecasts</CardTitle>
                  <BarChart3 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{processedForecasts.length || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    Across {scenarios.length || 0} scenarios
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Confidence</CardTitle>
                  <Target className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    {processedForecasts.length > 0 
                      ? Math.round(processedForecasts.reduce((sum: number, f: any) => sum + (f.confidence || 0), 0) / processedForecasts.length * 100)
                      : 0}%
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Prediction accuracy
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">AI Insights</CardTitle>
                  <Brain className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{aiInsights.length}</div>
                  <p className="text-xs text-muted-foreground">
                    {criticalInsights.length} critical alerts
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Forecast Range</CardTitle>
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{selectedPeriods}M</div>
                  <p className="text-xs text-muted-foreground">
                    Month projection period
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Forecast Trend Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Forecast Trends</CardTitle>
                <CardDescription>
                  AI-predicted budget trends with confidence intervals
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <Tooltip 
                      formatter={(value, name) => [
                        typeof value === 'number' ? `$${value.toLocaleString()}` : value,
                        name
                      ]}
                    />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="#8884d8"
                      strokeWidth={2}
                      dot={{ fill: '#8884d8' }}
                      name="Predicted Amount"
                    />
                    <Line
                      type="monotone"
                      dataKey="confidence"
                      stroke="#82ca9d"
                      strokeWidth={1}
                      strokeDasharray="5 5"
                      name="Confidence %"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scenario Analysis Tab */}
          <TabsContent value="scenarios" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Multi-Scenario Comparison</CardTitle>
                <CardDescription>
                  Compare baseline, optimistic, and pessimistic scenarios across categories
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={500}>
                  <BarChart data={scenarioComparison.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip formatter={(value) => `$${Number(value).toLocaleString()}`} />
                    <Legend />
                    <Bar dataKey="pessimistic" fill="#ff7c7c" name="Pessimistic" />
                    <Bar dataKey="baseline" fill="#8884d8" name="Baseline" />
                    <Bar dataKey="optimistic" fill="#82ca9d" name="Optimistic" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Scenario Cards */}
            <div className="grid gap-4 md:grid-cols-3">
              {scenarios.map((scenario: any, index: number) => (
                <Card key={scenario.id}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      {scenario.name}
                      <Badge variant={scenario.type === 'baseline' ? 'default' : scenario.type === 'optimistic' ? 'secondary' : 'outline'}>
                        {scenario.type}
                      </Badge>
                    </CardTitle>
                    <CardDescription>
                      Confidence: {Math.round(scenario.confidenceLevel * 100)}%
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Revenue Growth:</span>
                        <span className="font-medium">
                          {scenario.assumptions?.revenueGrowth || 0}%
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Expense Inflation:</span>
                        <span className="font-medium">
                          {scenario.assumptions?.expenseInflation || 0}%
                        </span>
                      </div>
                      <Progress 
                        value={scenario.confidenceLevel * 100} 
                        className="h-2"
                      />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Trend Analysis Tab */}
          <TabsContent value="trends" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Volatility Analysis</CardTitle>
                  <CardDescription>Risk assessment by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {processedForecasts
                      .filter((f: BudgetForecast) => f.scenario?.type === 'baseline')
                      .slice(0, 6)
                      .map((forecast: BudgetForecast) => (
                        <div key={forecast.id} className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{forecast.categoryName}</span>
                            {forecast.trendDirection === 'increasing' ? (
                              <TrendingUp className="h-4 w-4 text-green-500" />
                            ) : forecast.trendDirection === 'decreasing' ? (
                              <TrendingDown className="h-4 w-4 text-red-500" />
                            ) : (
                              <Activity className="h-4 w-4 text-gray-500" />
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <Progress 
                              value={forecast.volatilityScore * 100} 
                              className="w-20 h-2"
                            />
                            <span className="text-sm text-muted-foreground">
                              {Math.round(forecast.volatilityScore * 100)}%
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Confidence Distribution</CardTitle>
                  <CardDescription>Prediction accuracy by category</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={processedForecasts
                          .filter((f: BudgetForecast) => f.scenario?.type === 'baseline')
                          .slice(0, 5)
                          .map((f: BudgetForecast) => ({
                            name: f.categoryName,
                            value: Math.round(f.confidenceScore * 100)
                          }))}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, value }) => `${name}: ${value}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {processedForecasts.slice(0, 5).map((entry: any, index: number) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* AI Insights Tab */}
          <TabsContent value="insights" className="space-y-4">
            <div className="grid gap-4 md:grid-cols-1 lg:grid-cols-2">
              {aiInsights.map((insight: AIInsight) => {
                const IconComponent = getInsightIcon(insight.type);
                return (
                  <Card key={insight.id}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <IconComponent className="h-5 w-5" />
                          {insight.title}
                        </div>
                        <Badge variant={getSeverityColor(insight.severity)}>
                          {insight.severity}
                        </Badge>
                      </CardTitle>
                      <CardDescription>
                        Confidence: {Math.round(insight.confidenceScore * 100)}%
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm mb-3">{insight.description}</p>
                      
                      {insight.actionable && insight.suggestedActions.length > 0 && (
                        <div>
                          <p className="text-sm font-medium mb-2">Suggested Actions:</p>
                          <ul className="text-sm space-y-1">
                            {insight.suggestedActions.map((action, index) => (
                              <li key={index} className="flex items-start gap-2">
                                <span className="text-muted-foreground">•</span>
                                {action}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {aiInsights.length === 0 && (
              <Card>
                <CardContent className="text-center py-8">
                  <Brain className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Generate AI forecasts to receive intelligent insights and recommendations
                  </p>
                  <Button onClick={handleGenerateForecasts} className="mt-4">
                    Generate AI Insights
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
